import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { AppShell } from "./AppShell";
import { CompanyShell } from "./CompanyShell";
import { SuperadminShell } from "./SuperadminShell";
import { PosShell } from "./PosShell";
import { HomePage } from "@/modules/shell/pages/HomePage";
import { CompanyHomePage } from "@/modules/shell/pages/CompanyHomePage";
import { LoginPage } from "@/modules/auth/pages/LoginPage";
import { SelectCompanyPage } from "@/modules/auth/pages/SelectCompanyPage";
import { PlatformPage } from "@/modules/platform/pages/PlatformPage";
import { ProductsPage } from "@/modules/inventory/pages/ProductsPage";
import { ProductNewPage } from "@/modules/inventory/pages/ProductNewPage";
import { ProductDetailPage } from "@/modules/inventory/pages/ProductDetailPage";
import { LotInquiryPage } from "@/modules/inventory/pages/LotInquiryPage";
import { PurchaseOrdersPage } from "@/modules/purchasing/pages/PurchaseOrdersPage";
import { PurchaseOrderNewPage } from "@/modules/purchasing/pages/PurchaseOrderNewPage";
import { PurchaseOrderDetailPage } from "@/modules/purchasing/pages/PurchaseOrderDetailPage";
import { GoodsReceiptDetailPage } from "@/modules/purchasing/pages/GoodsReceiptDetailPage";
import { SupplierPaymentDetailPage } from "@/modules/purchasing/pages/SupplierPaymentDetailPage";
import { SupplierPaymentsPage } from "@/modules/purchasing/pages/SupplierPaymentsPage";
import { ToPayPage } from "@/modules/purchasing/pages/ToPayPage";
import { ToReceivePage } from "@/modules/purchasing/pages/ToReceivePage";
import { TerminalsPage } from "@/modules/pos/pages/TerminalsPage";
import { PosTicketsPage } from "@/modules/pos/pages/PosTicketsPage";
import { PosTicketDetailPage } from "@/modules/pos/pages/PosTicketDetailPage";
import { PosRegisterPage } from "@/modules/pos/pages/PosRegisterPage";
import { SalesOrdersPage } from "@/modules/sales/pages/SalesOrdersPage";
import { SalesOrderNewPage } from "@/modules/sales/pages/SalesOrderNewPage";
import { SalesOrderDetailPage } from "@/modules/sales/pages/SalesOrderDetailPage";
import { SalesDeliveryDetailPage } from "@/modules/sales/pages/SalesDeliveryDetailPage";
import { CustomerPaymentDetailPage } from "@/modules/sales/pages/CustomerPaymentDetailPage";
import { CustomerPaymentsPage } from "@/modules/sales/pages/CustomerPaymentsPage";
import { SalesInvoicesPage } from "@/modules/sales/pages/SalesInvoicesPage";
import { SalesInvoiceNewPage } from "@/modules/sales/pages/SalesInvoiceNewPage";
import { SalesInvoiceDetailPage } from "@/modules/sales/pages/SalesInvoiceDetailPage";
import { ContactDetailPage } from "@/modules/parties/pages/ContactDetailPage";
import { ContactsPage } from "@/modules/parties/pages/ContactsPage";
import { ToCollectPage } from "@/modules/sales/pages/ToCollectPage";
import { ToDeliverPage } from "@/modules/sales/pages/ToDeliverPage";
import { ReturnsPage } from "@/modules/returns/pages/ReturnsPage";
import { ReturnNewPage } from "@/modules/returns/pages/ReturnNewPage";
import { ReturnDetailPage } from "@/modules/returns/pages/ReturnDetailPage";
import { TrialBalancePage } from "@/modules/accounting/pages/TrialBalancePage";
import { AccountInquiryPage } from "@/modules/accounting/pages/AccountInquiryPage";
import { JournalEntryDetailPage } from "@/modules/accounting/pages/JournalEntryDetailPage";
import { ProfitLossPage } from "@/modules/accounting/pages/ProfitLossPage";
import { ChartOfAccountsPage } from "@/modules/accounting/pages/ChartOfAccountsPage";
import { ConvertPage } from "@/modules/inventory/pages/ConvertPage";
import { ConversionDetailPage } from "@/modules/inventory/pages/ConversionDetailPage";
import { InventoryPage } from "@/modules/inventory/pages/InventoryPage";
import { WarehousesPage } from "@/modules/inventory/pages/WarehousesPage";
import { ApprovalsPage } from "@/modules/overrides/pages/ApprovalsPage";
import { InboxPage } from "@/modules/comments/pages/InboxPage";
import { AuditPage } from "@/modules/audit/pages/AuditPage";
import { PayMethodsPage } from "@/modules/core/pages/PayMethodsPage";
import { PriceListsPage } from "@/modules/core/pages/PriceListsPage";
import { DocSeriesSettingsPage } from "@/modules/core/pages/DocSeriesSettingsPage";
import { OrgSettingsPage } from "@/modules/core/pages/OrgSettingsPage";
import { BranchesPage } from "@/modules/core/pages/BranchesPage";
import { BranchAccessPage } from "@/modules/core/pages/BranchAccessPage";

function RedirectContacts(props: { role?: "customer" | "supplier" }) {
  const { companySlug } = useParams();
  const qs = props.role ? `?role=${props.role}` : "";
  return <Navigate to={`/c/${companySlug}/contacts${qs}`} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* God-mode onboarding — own shell; never nested under company chrome */}
      <Route element={<SuperadminShell />}>
        <Route path="/superadmin" element={<PlatformPage />} />
        <Route path="/superadmin/*" element={<PlatformPage />} />
      </Route>
      <Route path="/platform" element={<Navigate to="/superadmin" replace />} />
      <Route path="/platform/*" element={<Navigate to="/superadmin" replace />} />
      <Route element={<AppShell />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/select-company" element={<SelectCompanyPage />} />
      </Route>
      <Route path="/c/:companySlug" element={<CompanyShell />}>
        <Route index element={<CompanyHomePage />} />
        <Route path="products" element={<ProductsPage />} />
        <Route path="products/new" element={<ProductNewPage />} />
        <Route path="products/:productId" element={<ProductDetailPage />} />
        <Route
          path="products/:productId/lots"
          element={<LotInquiryPage />}
        />
        <Route path="purchasing/orders" element={<PurchaseOrdersPage />} />
        <Route path="purchasing/orders/new" element={<PurchaseOrderNewPage />} />
        <Route
          path="purchasing/orders/:orderId"
          element={<PurchaseOrderDetailPage />}
        />
        <Route
          path="purchasing/receipts/:receiptId"
          element={<GoodsReceiptDetailPage />}
        />
        <Route
          path="purchasing/payments"
          element={<SupplierPaymentsPage />}
        />
        <Route
          path="purchasing/payments/:paymentId"
          element={<SupplierPaymentDetailPage />}
        />
        <Route
          path="purchasing/suppliers"
          element={<RedirectContacts role="supplier" />}
        />
        <Route path="purchasing/to-pay" element={<ToPayPage />} />
        <Route path="purchasing/to-receive" element={<ToReceivePage />} />
        <Route path="pos" element={<TerminalsPage />} />
        <Route path="pos/tickets" element={<PosTicketsPage />} />
        <Route
          path="pos/tickets/:ticketId"
          element={<PosTicketDetailPage />}
        />
        <Route path="contacts" element={<ContactsPage />} />
        <Route path="contacts/:partyId" element={<ContactDetailPage />} />
        <Route path="sales/orders" element={<SalesOrdersPage />} />
        <Route path="sales/orders/new" element={<SalesOrderNewPage />} />
        <Route path="sales/orders/:orderId" element={<SalesOrderDetailPage />} />
        <Route
          path="sales/deliveries/:deliveryId"
          element={<SalesDeliveryDetailPage />}
        />
        <Route path="sales/invoices" element={<SalesInvoicesPage />} />
        <Route path="sales/invoices/new" element={<SalesInvoiceNewPage />} />
        <Route
          path="sales/invoices/:invoiceId"
          element={<SalesInvoiceDetailPage />}
        />
        <Route path="sales/payments" element={<CustomerPaymentsPage />} />
        <Route
          path="sales/payments/:paymentId"
          element={<CustomerPaymentDetailPage />}
        />
        <Route
          path="sales/customers"
          element={<RedirectContacts role="customer" />}
        />
        <Route path="sales/to-collect" element={<ToCollectPage />} />
        <Route path="sales/to-deliver" element={<ToDeliverPage />} />
        <Route path="returns" element={<ReturnsPage />} />
        <Route path="returns/new" element={<ReturnNewPage />} />
        <Route path="returns/:returnId" element={<ReturnDetailPage />} />
        <Route path="gl/trial-balance" element={<TrialBalancePage />} />
        <Route path="gl/profit-loss" element={<ProfitLossPage />} />
        <Route path="gl/accounts" element={<ChartOfAccountsPage />} />
        <Route
          path="gl/accounts/:accountId"
          element={<AccountInquiryPage />}
        />
        <Route
          path="gl/journals/:journalId"
          element={<JournalEntryDetailPage />}
        />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="inventory/convert" element={<ConvertPage />} />
        <Route
          path="inventory/conversions/:conversionId"
          element={<ConversionDetailPage />}
        />
        <Route path="warehouses" element={<WarehousesPage />} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="settings/org" element={<OrgSettingsPage />} />
        <Route path="settings/pay-methods" element={<PayMethodsPage />} />
        <Route path="settings/price-lists" element={<PriceListsPage />} />
        <Route path="settings/doc-series" element={<DocSeriesSettingsPage />} />
        <Route path="settings/branches" element={<BranchesPage />} />
        <Route
          path="settings/branches/access"
          element={<BranchAccessPage />}
        />
      </Route>
      <Route path="/c/:companySlug/b/:branchSlug/pos/t/:terminalId" element={<PosShell />}>
        <Route index element={<PosRegisterPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
